<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import bg from '@/assets/img/team/team_bg.svg'
import bgElem1 from '@/assets/img/team/team_bg-elem-1.png'

const bgElems = [{ src: bgElem1, cx: 100, cy: 100, a: 50 }]

import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/navigation'

const swiperRef = ref(null)

const props = defineProps({
	items: {
		type: Array,
		required: true,
		default: () => [],
	},
})

const isMobile = ref(window.innerWidth < 992)
const handleResize = () => {
	isMobile.value = window.innerWidth < 992
	visibleSlideCount.value = 1
}
onMounted(() => {
	window.addEventListener('resize', handleResize)
	handleResize()
})
onBeforeUnmount(() => {
	window.removeEventListener('resize', handleResize)
})

function chunkArray(array, size) {
	const result = []
	for (let i = 0; i < array.length; i += size) {
		result.push(array.slice(i, i + size))
	}
	return result
}
const chunkedSlides = computed(() => chunkArray(props.items, 6))

const visibleSlideCount = ref(1)
const totalSlides = computed(() => chunkedSlides.value.length)
const showMoreButton = computed(
	() => isMobile.value && totalSlides.value > 1 && visibleSlideCount.value < totalSlides.value,
)

const handleShowMore = () => {
	if (visibleSlideCount.value < totalSlides.value) {
		visibleSlideCount.value += 1
	}
}

function splitName(fullName) {
	if (!fullName) return []
	return fullName.trim().split(' ')
}

watch(
	() => props.items,
	async () => {
		await nextTick()
		swiperRef.value?.swiper?.navigation?.init()
		swiperRef.value?.swiper?.navigation?.update()
	},
)

const hoveredIndex = ref(null)
const randomHoverIndex = ref(null)
onMounted(() => {
	randomHoverIndex.value = Math.floor(Math.random() * 6) + 1 // от 1 до 6
})
</script>

<template>
	<section class="team" id="team">
		<div
			class="team__bg"
			v-parallax
			:data-prlx-cx="1000"
			:data-prlx-cy="1000"
			:data-prlx-a="50"
			:data-prlx-dxr="true"
			:data-prlx-dyr="true"
		>
			<img :src="bg" alt="" />
		</div>

		<div
			v-for="(image, index) in bgElems"
			:key="index"
			:class="`team__bg-elem team__bg-elem-${index + 1}`"
			v-parallax
			:data-prlx-cx="image.cx"
			:data-prlx-cy="image.cy"
			:data-prlx-a="image.a"
			:data-prlx-dxr="image.dxr ? '' : null"
			:data-prlx-dyr="image.dyr ? '' : null"
		>
			<img :src="image.src" alt="" />
		</div>

		<div class="team__container">
			<div class="team__block">
				<div class="section-header">
					<h2>Команда</h2>
				</div>

				<div class="team__wrapper">
					<div class="team__content">
						<div class="team__body">
							<Swiper
								v-if="!isMobile"
								ref="swiperRef"
								class="team__slider"
								:modules="[Navigation]"
								:slides-per-view="1"
								:space-between="750"
								:navigation="{
									prevEl: '.team__button-prev',
									nextEl: '.team__button-next',
								}"
							>
								<SwiperSlide
									v-for="(group, slideIndex) in chunkedSlides"
									:key="slideIndex"
									class="team__slide"
									:class="[
										hoveredIndex !== null
											? `hover-${hoveredIndex + 1}`
											: `hover-${randomHoverIndex}`,
									]"
								>
									<div v-for="item in group" :key="item.id" class="team__item">
										<div class="team__img">
											<picture>
												<source
													:srcset="item.image_desktop"
													media="(min-width: 991.98px)"
												/>
												<img :src="item.image_mobile" :alt="item.name" />
											</picture>
										</div>
										<div class="team__desc">
											<h4>
												<span
													v-for="(part, index) in splitName(item.name)"
													:key="index"
													>{{ part }}</span
												>
											</h4>
											<h5>{{ item.job_title }}</h5>
										</div>
									</div>
								</SwiperSlide>
							</Swiper>

							<div v-else class="team__slider team__slider--mobile">
								<div
									v-for="(group, slideIndex) in chunkedSlides"
									:key="slideIndex"
									class="team__slide"
									v-show="slideIndex < visibleSlideCount"
								>
									<div v-for="item in group" :key="item.id" class="team__item">
										<div class="team__img">
											<picture>
												<img :src="item.image_mobile" :alt="item.name" />
											</picture>
										</div>
										<div class="team__desc">
											<h4>
												<span
													v-for="(part, index) in splitName(item.name)"
													:key="index"
													>{{ part }}</span
												>
											</h4>
											<h5>{{ item.job_title }}</h5>
										</div>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="team__btn" v-if="isMobile && showMoreButton">
					<button type="button" class="btn _btn-second" @click="handleShowMore">
						<div class="btn__text">Смотреть еще</div>
						<svg
							class="btn__shadow"
							preserveAspectRatio="none"
							viewBox="0 0 320 72"
							fill="none"
						>
							<path d="M10 72L0 62H310L320 72L10 72Z" fill="#818B8F" />
						</svg>
					</button>
				</div>

				<div class="team__action" v-if="!isMobile">
					<div class="team__buttons">
						<button class="arrow-btn team__button-prev" type="button">
							<div class="arrow-btn__icon">
								<svg viewBox="0 0 100 100">
									<path
										d="M62.8328 83.5L28.5 49.5074L61.8379 16.5L71.7657 26.3294L48.0956 49.7655L72.5 73.9287L62.8328 83.5Z"
										fill="#F27699"
									/>
								</svg>
							</div>
							<svg
								class="arrow-btn__shadow"
								preserveAspectRatio="none"
								viewBox="0 0 130 126"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
								<path
									d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
									fill="#3A3E40"
								/>
							</svg>
						</button>
						<button class="arrow-btn team__button-next" type="button">
							<div class="arrow-btn__icon">
								<svg viewBox="0 0 100 100">
									<path
										d="M38.1671 16.5L72.5 50.4926L39.1621 83.5L29.2344 73.6706L52.9044 50.2345L28.5 26.0713L38.1671 16.5Z"
										fill="#F27699"
									/>
								</svg>
							</div>
							<svg
								class="arrow-btn__shadow"
								preserveAspectRatio="none"
								viewBox="0 0 130 126"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
								<path
									d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
									fill="#3A3E40"
								/>
							</svg>
						</button>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.team {
	position: relative;
	padding: 9rem 0 10.5rem;
	overflow: hidden;

	@media (max-width: 991.98px) {
		padding: 1.4rem 0 6.6rem;
	}

	// .team__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	&__bg-elem {
		position: absolute;
		z-index: 10;
		will-change: transform;
		transition: transform 0.2s ease-out;
		pointer-events: none;

		&-1 {
			@media (min-width: 991.98px) {
				width: 30.9rem;
				height: 30.7rem;
				top: 23.3rem;
				left: 4.8rem;
			}

			@media (max-width: 991.98px) {
				display: none;
			}
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .team__container
	&__container {
	}

	// .team__block
	&__block {
		position: relative;
	}

	// .team__wrapper
	&__wrapper {
		position: relative;

		@media (max-width: 991.98px) {
			margin-top: 3.8rem;
		}

		@media (min-width: 991.98px) {
			margin-top: -0.9rem;

			&::after {
				content: '';
				position: absolute;
				width: 100vw;
				height: 12rem;
				z-index: -1;
				background-color: #f06793;
				top: 41.7%;
				left: 50%;
				transform: translate(-50%, -50%);
			}
		}
	}

	// .team__content
	&__content {
	}

	// .team__body
	&__body {
	}

	&__slider {
		overflow: visible;

		@media (min-width: 991.98px) {
			max-width: 120rem;
			margin: 0 auto;
		}
	}

	// .team__slide
	&__slide {
		display: grid;
		grid-template-columns: repeat(5, 1fr);

		@media (min-width: 991.98px) {
			grid-template-rows: repeat(3, 1fr);
			height: calc(24rem * 3);
		}

		@media (max-width: 991.98px) {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	&__item {
		transition: all 0.3s ease;
		align-self: end;
		position: relative;
		z-index: 1;
		overflow: hidden;
		transform-origin: left bottom;

		@media (max-width: 991.98px) {
			min-height: 27rem;

			&:nth-child(3),
			&:nth-child(6) {
				grid-column: 1/3;
			}
		}

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 1;
			pointer-events: none;
			background: rgba(0, 0, 0, 0.7);
			opacity: 0;
			visibility: hidden;
			transition: all 0.3s ease 0s;

			@media (max-width: 991.98px) {
				background: linear-gradient(0deg, #000 0%, rgba(0, 0, 0, 0) 70%);
				opacity: 1;
				visibility: visible;
			}
		}

		@media (min-width: 991.98px) {
			aspect-ratio: 1 / 1;
			width: 24rem;

			&.active {
				&::before {
					opacity: 1;
					visibility: visible;
				}
			}

			@media (any-hover: hover) {
				&:hover {
					.team__desc {
						opacity: 1;
						visibility: visible;
					}

					&::before {
						opacity: 1;
						visibility: visible;
					}
				}
			}

			.hover-1.swiper-slide-active &,
			.hover-6.swiper-slide-active & {
				&:nth-child(1) {
					grid-row: 1/3;
					grid-column: 1/3;
					width: 48rem;
				}

				&:nth-child(2) {
					grid-row: 1/2;
					grid-column: 3/4;
				}

				&:nth-child(3) {
					grid-row: 1/2;
					grid-column: 4/5;
				}

				&:nth-child(4) {
					grid-column: 5/6;
					grid-row: 1/3;
					margin-bottom: 25.3%;
				}

				&:nth-child(5) {
					grid-column: 2/3;
					grid-row: 3/4;
				}

				&:nth-child(6) {
					grid-column: 3/5;
					grid-row: 2/4;
					width: 48rem;
				}
			}

			.hover-2.swiper-slide-active &,
			.hover-5.swiper-slide-active & {
				&:nth-child(1) {
					grid-row: 1/2;
				}

				&:nth-child(2) {
					grid-row: 1/3;
					grid-column: 3/5;
					width: 48rem;
				}

				&:nth-child(3) {
					grid-row: 1/3;
					grid-column: 5/6;
				}

				&:nth-child(4) {
					grid-column: 4/5;
					grid-row: 3/4;
				}

				&:nth-child(5) {
					grid-column: 1/3;
					grid-row: 2/4;
					width: 48rem;
				}

				&:nth-child(6) {
					grid-column: 3/4;
					grid-row: 3/4;
				}
			}

			.hover-3.swiper-slide-active &,
			.hover-4.swiper-slide-active & {
				&:nth-child(1) {
					grid-row: 2/3;
				}

				&:nth-child(2) {
					grid-row: 2/3;
				}

				&:nth-child(3) {
					grid-row: 1/3;
					grid-column: 3/5;
					width: 48rem;
				}

				&:nth-child(4) {
					grid-column: 5/6;
					grid-row: 1/3;
					width: 48rem;
					margin-right: -100%;
					margin-bottom: -25.3%;
				}

				&:nth-child(5) {
					grid-column: 2/3;
					grid-row: 3/4;
				}

				&:nth-child(6) {
					grid-column: 4/5;
					grid-row: 3/4;
				}
			}
		}
	}

	// .team__img
	&__img {
		width: 100%;
		height: 100%;

		@media (max-width: 991.98px) {
			position: absolute;
			top: 0;
			left: 0;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;

			@media (min-width: 991.98px) {
				filter: grayscale(0);
				transition: filter 0.3s ease 0s;

				.active & {
					filter: grayscale(1);
				}
			}
		}
	}

	// .team__desc
	&__desc {
		padding: 2rem 2.4rem;
		position: absolute;
		z-index: 2;
		top: 0;
		left: 0;
		transition: all 0.3s ease 0s;

		@media (max-width: 991.98px) {
			bottom: 0;
			top: auto;
		}

		h4 {
			display: inline-flex;
			flex-wrap: wrap;
			gap: 0 0.8rem;

			span {
				display: inline-block;
				overflow-wrap: break-word;
				word-break: normal;
				hyphens: auto;
			}
		}

		h5 {
			margin-top: 0.8rem;
		}

		@media (max-width: 991.98px) {
			padding: 1.2rem 1.6rem;
		}

		@media (min-width: 991.98px) {
			opacity: 0;
			visibility: hidden;

			.active & {
				opacity: 1;
				visibility: visible;
			}
		}
	}

	// .team__action
	&__action {
		position: absolute;
		bottom: 0;
		left: -2.4%;

		@media (max-width: 991.98px) {
			display: none;
		}
	}

	// .team__buttons
	&__buttons {
		display: flex;
		gap: 2rem;
	}

	&__btn {
		width: 100%;
		margin-top: 2.4rem;

		@media (min-width: 991.98px) {
			display: none;
		}

		.btn {
			width: 100%;
		}
	}
}

@media (max-width: 991.98px) {
	.team__slider--mobile {
		display: block !important;
	}
}
</style>
